// Licence card at the top of the Super Admin page (customer-hosted installs only). Shows the
// installed offline licence from GET /licence (fetched by SuperAdmin.js and passed in) and uploads
// a new .lic file to POST /licence. Hidden when the install needs no licence (the SaaS).
import React, { useRef, useState } from "react";
import { Key, Upload } from "react-feather";
import { useApi } from "../hooks/useApi";
import { formatLicenceDate } from "./LicenceBanner";

const STATE_PILLS = {
  valid: { label: "Valid", className: "bg-status-success-bg text-status-success" },
  expiringSoon: { label: "Expiring", className: "bg-status-warning-bg text-status-warning" },
  grace: { label: "Grace period", className: "bg-status-warning-bg text-status-warning" },
  readOnly: { label: "Expired (read-only)", className: "bg-status-error-bg text-status-error" },
  none: { label: "No licence", className: "bg-status-error-bg text-status-error" },
};

const PRODUCT_LABELS = {
  Deals: "Deals",
  BusinessUnits: "Business Units",
  Enterprise: "Enterprise",
  TPRM: "TPRM",
  Ransomware: "Ransomware",
  DarkWeb: "Dark Web",
  AI: "AI",
  GRC: "GRC",
};

const describeDaysRemaining = (days) => {
  if (days === null || days === undefined) return null;
  if (days >= 0) return `${days} ${days === 1 ? "day" : "days"} remaining`;
  const ago = -days;
  return `expired ${ago} ${ago === 1 ? "day" : "days"} ago`;
};

const LicenceCard = ({ licence, onUploaded }) => {
  const apiCall = useApi();
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const [uploadSuccess, setUploadSuccess] = useState(false);

  if (!licence || licence.required !== true) return null;

  const handleFileChange = async (event) => {
    const file = event.target.files && event.target.files[0];
    // Reset so choosing the same file again still fires a change.
    event.target.value = "";
    if (!file) return;

    setUploading(true);
    setUploadError(null);
    setUploadSuccess(false);
    try {
      const formData = new FormData();
      formData.append("file", file);
      // isFormData=true: without it useApi sends application/json and the request reaches the
      // JSON upload action, which answers "No licence file was provided."
      const data = await apiCall("POST", "/licence", formData, true);
      setUploadSuccess(true);
      if (onUploaded) await onUploaded(data);
    } catch (err) {
      setUploadError(err?.response?.data?.message || "Failed to upload the licence.");
    } finally {
      setUploading(false);
    }
  };

  const pill = STATE_PILLS[licence.state] || STATE_PILLS.none;
  const daysText = describeDaysRemaining(licence.daysRemaining);
  const organizationsText =
    licence.maxOrganizations === null || licence.maxOrganizations === undefined
      ? `${licence.organizationsUsed ?? 0} of unlimited`
      : `${licence.organizationsUsed ?? 0} of ${licence.maxOrganizations}`;
  const products = licence.products || [];

  return (
    <div
      className="mb-6 bg-theme-secondary border border-border rounded-xl p-6"
      data-testid="licence-card"
    >
      <div className="flex flex-wrap items-start justify-between gap-4 mb-4">
        <div className="flex items-center gap-3">
          <Key className="h-5 w-5 text-content-secondary" aria-hidden="true" />
          <h2 className="text-lg font-semibold text-content-primary">Licence</h2>
          <span
            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${pill.className}`}
          >
            {pill.label}
          </span>
        </div>
        <div>
          <input
            ref={fileInputRef}
            type="file"
            accept=".lic"
            className="hidden"
            aria-label="Licence file"
            onChange={handleFileChange}
          />
          <button
            type="button"
            onClick={() => fileInputRef.current && fileInputRef.current.click()}
            disabled={uploading}
            className="flex items-center gap-2 px-4 py-2 text-sm text-white bg-gradient-primary rounded-lg hover:shadow-glow-cyan transition-all disabled:opacity-50"
          >
            <Upload className="h-4 w-4" />
            {uploading ? "Uploading..." : "Upload licence"}
          </button>
        </div>
      </div>

      {uploadSuccess && (
        <div className="mb-4 p-3 bg-status-success-bg rounded-lg text-status-success text-sm" role="status">
          Licence installed.
        </div>
      )}
      {uploadError && (
        <div className="mb-4 p-3 bg-risk-critical/10 border border-risk-critical/30 rounded-lg text-risk-critical text-sm" role="alert">
          {uploadError}
        </div>
      )}

      {licence.state === "none" ? (
        <p className="text-sm text-content-secondary">
          No licence is installed. Products stay off until a licence is uploaded.
          {" "}Organizations: {organizationsText}.
        </p>
      ) : (
        <dl className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 text-sm">
          <div>
            <dt className="text-content-secondary">Customer</dt>
            <dd className="text-content-primary font-medium">{licence.customerName || "—"}</dd>
          </div>
          <div>
            <dt className="text-content-secondary">Expires</dt>
            <dd className="text-content-primary font-medium">
              {formatLicenceDate(licence.expiresAt) || "—"}
              {daysText && <span className="block text-xs text-content-muted">{daysText}</span>}
            </dd>
          </div>
          <div>
            <dt className="text-content-secondary">Organizations</dt>
            <dd className="text-content-primary font-medium">{organizationsText}</dd>
          </div>
          <div>
            <dt className="text-content-secondary">Products</dt>
            <dd className="text-content-primary">
              {products.length === 0 ? (
                "—"
              ) : (
                <ul className="space-y-0.5">
                  {products.map((p) => (
                    <li key={p.product}>
                      {PRODUCT_LABELS[p.product] || p.product}
                      <span className="text-content-muted">
                        {" "}({p.assessmentsPerOrganization} assessments per organization)
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </dd>
          </div>
        </dl>
      )}
    </div>
  );
};

export default LicenceCard;
